import React from "react";
import { Section } from "@/javascript/music-objects";

export interface MidiNoteBlock {
    id: string;
    x: number;
    y: number;
    width: number;
    height: number;
}

interface MidiBlocksProps {
    section: Section;
    trimStart?: number;
    trimEnd?: number;
}

const FIXED_MEASURE_WIDTH = 50;
const FIXED_HEIGHT = 300;

const MidiBlocks: React.FC<MidiBlocksProps> = React.memo(
    ({ section, trimStart, trimEnd }) => {
        // Process midi measures and compute note positions.
        const midiMeasures = section.toMidiMeasures(trimStart, trimEnd);
        const allMidiNotes = midiMeasures.flatMap((m) => m.notes);
        if (allMidiNotes.length === 0) return null;

        const minNote = Math.min(...allMidiNotes.map((n) => n.midi));
        const maxNote = Math.max(...allMidiNotes.map((n) => n.midi));
        const padding = FIXED_HEIGHT / 10;
        const noteScale = (FIXED_HEIGHT - padding * 2) / (maxNote - minNote + 1);
        const blocks: MidiNoteBlock[] = [];
        midiMeasures.forEach((midiMeasure, measureIndex) => {
            const measureTimeScale = FIXED_MEASURE_WIDTH / midiMeasure.timeSignature.beats;
            const measureOffset = measureIndex * FIXED_MEASURE_WIDTH;
            midiMeasure.notes.forEach((midiNote) => {
                const x = measureOffset + midiNote.startBeat * measureTimeScale;
                const y = FIXED_HEIGHT - (midiNote.midi - minNote + 1) * noteScale - padding;
                const rectWidth = Math.max(
                    midiNote.getBeatDuration() * measureTimeScale - 2,
                    2
                );
                const rectHeight = Math.min(noteScale, 3);
                blocks.push({
                    id: midiNote.id,
                    x,
                    y,
                    width: rectWidth,
                    height: rectHeight,
                });
            });
        });
        
        const viewBox = `0 0 ${FIXED_MEASURE_WIDTH * midiMeasures.length} ${FIXED_HEIGHT}`;

        return (
            <div
                style={{
                    height: "100%",
                    width: "100%",
                    display: "block"
                }}
            >
                <svg
                    viewBox={viewBox}
                    preserveAspectRatio="none"
                    style={{
                        height: "100%",
                        width: "100%",
                        display: "block"
                    }}
                >
                    {blocks.map((note) => (
                        <rect
                            key={note.id}
                            className={`note-${note.id}`}
                            x={note.x}
                            y={note.y - note.height * 2}
                            width={note.width}
                            height={note.height * 4}
                            fill="black"
                        />
                    ))}
                </svg>
            </div>
        );
    },
    (prevProps, nextProps) =>
        prevProps.section.id === nextProps.section.id &&
        prevProps.section.getHash() === nextProps.section.getHash()
);

export default MidiBlocks;
